/**
* PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
* Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Pearson Education, Inc.  The intellectual and technical concepts contained
* herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
* patent applications, and are protected by trade secret or copyright law.
* Dissemination of this information, reproduction of this material, and copying or distribution of this software
* is strictly forbidden unless prior written permission is obtained
* from Pearson Education, Inc.
*/
/**
* Validation utilities
*
* @file ValidationUtils.js
* @author Satyaki Bose
*/
import Utils from '../utils';
import { parseDate, toFormTime } from '../dateTimeUtils';
import {
  TIMEFIELD_CONSTANTS,
  VALIDATEDATE_CONSTANTS,
  PREFIX_ASSIGNMENT,
  DATE_NORMALIZATION_ACTION,
  COVERAGE_DEDUPE_ACTION
} from '../constants';

export default class ValidationUtils {
  /**
     * Combines date and time strings into a Date object
     * @returns {Date|null} - Combined Date object or null if invalid
     */
  static combineDateAndTime = (dateString, timeString = TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME) => {
    if (!dateString) return null;
    const dateObj = parseDate(dateString);
    if (timeString && timeString.includes(':')) {
      const [hours, minutes] = timeString.split(':').map(Number);
      dateObj.setHours(hours, minutes, 0, 0);
    } else {
      // Default to end of day if no time provided
      dateObj.setHours(23, 59, 0, 0);
    }

    return dateObj;
  };

  /**
     * Validates if a date (and optionally time) is missing or in the past
     * @returns {Object|null} - Error object if invalid, null if valid
     */
  static validateDate = (dateString, timeString = null) => {
    // Check if date is missing
    if (!dateString) {
      return {
        type: VALIDATEDATE_CONSTANTS.NO_DATE,
        message: VALIDATEDATE_CONSTANTS.DUE_DATE_REQUIRED
      };
    }
    const currentDateTime = new Date();
    // If time is provided, check date + time together
    if (timeString) {
      const dueDateTime = ValidationUtils.combineDateAndTime(dateString, timeString);
      if (!dueDateTime) {
        return {
          type: VALIDATEDATE_CONSTANTS.INVALID_DATE,
          message: VALIDATEDATE_CONSTANTS.INVALID_DATE_FORMAT
        };
      }
      // Compare full datetime
      if (dueDateTime < currentDateTime) {
        return {
          type: VALIDATEDATE_CONSTANTS.PAST_DUE_DATE,
          message: VALIDATEDATE_CONSTANTS.DUE_DATE_PAST
        };
      }
    } else {
      // If no time provided, just check date (for backward compatibility)
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const dueDate = parseDate(dateString);
      dueDate.setHours(0, 0, 0, 0);
      if (dueDate < currentDate) {
        return {
          type: VALIDATEDATE_CONSTANTS.PAST_DUE_DATE,
          message: VALIDATEDATE_CONSTANTS.DUE_DATE_IN_THE_PAST
        };
      }
    }

    return null; // Valid date
  };

  /**
   * Validates due time is present (assignment form mandatory field).
   * @returns {Object|null} - Error object if invalid, null if valid
   */
  static validateDueTime = (timeString) => {
    if (!timeString || String(timeString).trim() === '') {
      return {
        type: VALIDATEDATE_CONSTANTS.NO_TIME,
        message: VALIDATEDATE_CONSTANTS.DUE_TIME_REQUIRED
      };
    }

    return null;
  };

  /**
     * Validates if chapters are properly mapped in the TOC
     * @returns {Object|null} - Error object if invalid, null if valid
     */
  static validateMappedChapters = (assignment, tocData = []) => {
    let hasMappedChapters = false;
    // Try to resolve chapters from textbookCoverage
    if (assignment.textbookCoverage && tocData && tocData.length > 0) {
      const rehydratedNodes = Utils.rehydrateSelectedNodesFromCoverage(
        assignment.textbookCoverage,
        tocData,
        { preferPages: true }
      );
      hasMappedChapters = rehydratedNodes && rehydratedNodes.length > 0;
    }
    if (!hasMappedChapters) {
      return {
        type: VALIDATEDATE_CONSTANTS.NO_CHAPTERS,
        message: VALIDATEDATE_CONSTANTS.NO_PROPERLY_CHAPTERS
      };
    }

    return null; // Valid chapters
  };

  static hasNoChaptersError = (errors = []) => (
    errors.some(error => error.type === VALIDATEDATE_CONSTANTS.NO_CHAPTERS)
  );

  /**
   * Returns a NO_QUESTIONS error when selected chapters have zero MCQs, otherwise null.
   */
  static getNoQuestionsError = (chapters, tocData, chapterMcqMapping) => {
    if (!chapterMcqMapping || !chapters?.length) return null;

    const { questions } = Utils.calculateSectionsAndQuestions(
      { chapters },
      chapterMcqMapping,
      tocData
    );

    if (questions === 0) {
      return {
        type: VALIDATEDATE_CONSTANTS.NO_QUESTIONS,
        message: VALIDATEDATE_CONSTANTS.SELECTED_CHAPTERS_QUESTIONS
      };
    }

    return null;
  };

  /**
   * Status column for confirm/publish table — warnings first, then info.
   */
  static getAssignmentRowStatus = (assignment, textInfo = {}, assignmentErrors = []) => {
    const warnings = [];

    if (ValidationUtils.hasNoChaptersError(assignmentErrors)) {
      warnings.push(textInfo.unassignedCoverageStatus || '');
    }

    if (assignment?.dateNormalizationAction === DATE_NORMALIZATION_ACTION.MANUAL_ENTRY_REQUIRED) {
      warnings.push(textInfo.dateNormalizationManualEntryStatus || '');
    }

    // putting more priority to warnings
    if (warnings.length > 0) {
      return { icon: 'warning', message: warnings[0] };
    }

    if (assignment?.dateNormalizationAction === DATE_NORMALIZATION_ACTION.YEAR_ADJUSTED) {
      return {
        icon: 'info',
        message: textInfo.dateNormalizationYearAdjustedStatus || ''
      };
    }

    if (assignment?.coverageDedupeAction === COVERAGE_DEDUPE_ACTION.ADJUSTED) {
      return {
        icon: 'info',
        message: textInfo.coverageDedupeAdjustedStatus || ''
      };
    }

    return null;
  };

  /**
     * Validates a single assignment
     * @param {Object} assignment - The assignment to validate
     * @param {Array} tocData - The TOC data
     * @param {Array} chapterMcqMapping - Optional MCQ mapping to validate question counts
     * @returns {Array} - Array of error objects
     */
  static validateSingleAssignment = (assignment, tocData = [], chapterMcqMapping = null) => {
    const assignmentErrors = [];

    // Validation 1: Check date and time
    const dateError = ValidationUtils.validateDate(assignment.date, assignment.time);
    if (dateError) {
      assignmentErrors.push(dateError);
    }

    // Validation 2: Check chapters
    const chapterError = ValidationUtils.validateMappedChapters(assignment, tocData);
    if (chapterError) {
      assignmentErrors.push(chapterError);
    }

    // Validation 3: Check if chapters have questions (if MCQ mapping provided)
    if (chapterMcqMapping) {
      let chapters = assignment.chapters;

      if (!chapters || chapters.length === 0) {
        if (assignment.textbookCoverage && tocData && tocData.length > 0) {
          chapters = Utils.rehydrateSelectedNodesFromCoverage(
            assignment.textbookCoverage,
            tocData,
            { preferPages: true }
          );
        }
      }

      const questionsError = ValidationUtils.getNoQuestionsError(chapters, tocData, chapterMcqMapping);
      if (questionsError) {
        assignmentErrors.push(questionsError);
      }
    }

    return assignmentErrors;
  };

  /**
     * Validates multiple assignments in a schedule
     * @param {Array} schedule - Array of assignments to validate
     * @param {Array} tocData - The TOC data
     * @returns {Object} - Validation result with errors and invalid assignments
     */
  static validateAssignments = (schedule = [], tocData = [], chapterMcqMapping = null) => {
    const errors = [];
    const invalidAssignments = [];
    schedule.forEach((assignment, index) => {
      const assignmentErrors = ValidationUtils.validateSingleAssignment(assignment, tocData, chapterMcqMapping);
      if (assignmentErrors.length > 0) {
        invalidAssignments.push({
          index,
          id: assignment.id || `${PREFIX_ASSIGNMENT}-${index}`,
          title: assignment.title,
          errors: assignmentErrors
        });
        errors.push(...assignmentErrors);
      }
    });

    return {
      isValid: errors.length === 0,
      invalidAssignments,
      errors,
      totalCount: schedule.length,
      invalidCount: invalidAssignments.length,
      validAssignments: schedule.filter((_, index) => (
        !invalidAssignments.some(inv => inv.index === index)
      ))
    };
  };

  /**
 * Validates assignment title.
 *
 * Validation Rules:
 * 1. Title is required (cannot be empty or only spaces)
 * 2. Title must not exceed MAX_ASSIGNMENT_TITLE_LENGTH
 * 3. Title must contain only supported characters
 *    (letters, numbers, spaces, underscore)
 *
 * @param {string} title - Assignment title entered by user
 * @returns {Object|null} Returns error object if invalid, otherwise null
 */
  static validateAssignmentTitle = (title, existingTitles = [], titleValidationConfig = {}) => {
    const trimmedTitle = title?.trim();

    if (!trimmedTitle) {
      return {
        type: 'TITLE_REQUIRED',
        message: titleValidationConfig.titleRequired
      };
    }

    if (trimmedTitle.length > titleValidationConfig.maxLength) {
      return {
        type: 'TITLE_TOO_LONG',
        message: titleValidationConfig.titleTooLong
      };
    }

    const supportedChar = new RegExp(titleValidationConfig.supportedCharRegex);
    if (supportedChar.test(trimmedTitle)) {
      return {
        type: 'TITLE_INVALID_CHAR',
        message: titleValidationConfig.titleInvalidChar
      };
    }

    const lowerTrimmedTitle = trimmedTitle.toLowerCase();
    const duplicate = existingTitles.find(
      (existingTitle) => existingTitle.toLowerCase() === lowerTrimmedTitle
    );

    if (duplicate) {
      return {
        type: 'TITLE_DUPLICATE',
        message: titleValidationConfig.titleDuplicate
      };
    }

    return null;
  };

  /**
     * Validates form data for a single assignment being edited/created
     * @returns {Object} - { hasDateError: boolean, hasChapterError: boolean, errors: Array }
     */
  static validateAssignmentForm = (
    formData,
    tocData = [],
    chapterMcqMapping = [],
    excludeTitles,
    showAssignmentTitle,
    titleValidationConfig
  ) => {
    const errors = [];
    // Validation 0: Title
    // Only validate title if visible
    let titleError = null;
    if (showAssignmentTitle) {
      titleError = ValidationUtils.validateAssignmentTitle(formData.title, excludeTitles, titleValidationConfig);
      if (titleError) {
        errors.push(titleError);
      }
    }
    // Validation 1: Check date AND time using the extracted function
    const dateError = ValidationUtils.validateDate(formData.date, formData.time);
    if (dateError) {
      errors.push(dateError);
    }
    // Validation 2: Check chapters
    const tempAssignment = {
      date: formData.date,
      time: formData.time,
      chapters: formData.chapters,
      textbookCoverage: formData.chapters && formData.chapters.length > 0
        ? Utils.createTextbookCoverage(formData.chapters, tocData)
        : null
    };
    const chapterError = ValidationUtils.validateMappedChapters(tempAssignment, tocData);
    if (chapterError) {
      errors.push(chapterError);
    }
    // Additional validation: Check if chapters have questions
    const questionsError = ValidationUtils.getNoQuestionsError(
      formData.chapters,
      tocData,
      chapterMcqMapping
    );
    const hasNoQuestions = !!questionsError;
    if (questionsError) {
      errors.push(questionsError);
    }

    return {
      hasDateError: !!dateError,
      hasChapterError: !!chapterError || hasNoQuestions,
      hasAssignmentTitleError: !!titleError,
      titleError: titleError?.message,
      hasNoQuestions,
      errors,
      isValid: errors.length === 0,
      titleHelperText: titleValidationConfig?.titleHelperText || ''
    };
  };

  /**
   * One-time schedule processing after syllabus upload (chapter filter, dates, validation).
   */
  static processScheduleAfterUpload({
    schedule = [],
    tocData,
    chapterMcqMapping,
    restrictAlreadyAssigned,
    syllabusDateCallbacks
  }) {
    let processedSchedule = Utils.validateAssignmentChapters(
      schedule,
      tocData,
      restrictAlreadyAssigned
    );

    let dateNormalizationSummary = null;

    if (syllabusDateCallbacks?.normalizeResponseDates) {
      const normalized = syllabusDateCallbacks.normalizeResponseDates({
        data: { schedule: processedSchedule },
        assignments: processedSchedule
      });
      if (normalized?.schedule) {
        processedSchedule = normalized.schedule;
      }
      dateNormalizationSummary = normalized.dateNormalizationSummary || null;
    }

    processedSchedule = processedSchedule.map((item) => ({
      ...item,
      time: toFormTime(item?.time) ?? ''
    }));

    let validationResult = {
      isValid: true,
      invalidAssignments: [],
      errors: [],
      totalCount: processedSchedule.length,
      invalidCount: 0
    };

    if (processedSchedule.length > 0 && tocData) {
      validationResult = ValidationUtils.validateAssignments(
        processedSchedule,
        tocData,
        chapterMcqMapping
      );
    }

    const validatedAssignments = processedSchedule.map((assignment, idx) => {
      const invalidItem = validationResult.invalidAssignments.find(inv => inv.index === idx);

      return {
        ...assignment,
        isValid: !invalidItem,
        validationErrors: invalidItem?.errors || [],
        dateNormalizationAction: assignment.dateNormalizationAction ?? null,
        coverageDedupeAction: assignment.coverageDedupeAction ?? null
      };
    });

    return { validatedAssignments, validationResult, dateNormalizationSummary };
  }
}
